import { useState } from "react";
import { Link } from "react-router-dom";

export default function Converter() {
  const [files, setFiles] = useState([]);
  const [results, setResults] = useState([]);
  const [converting, setConverting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  /* ===== FILE HANDLERS ===== */
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
    }
  };

  const addFiles = (newFiles) => {
    const validJpgs = newFiles.filter(
      (file) => file.type.includes("jpg") || file.type.includes("jpeg") || file.name.match(/\.(jpg|jpeg)$/i)
    );
    setFiles((prev) => [...prev, ...validJpgs]);
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setResults([]);
  };

  /* ===== CONVERT LOGIC ===== */
  const convertAll = async () => {
    if (!files.length) return;

    setConverting(true);
    setResults([]);

    const newResults = [];
    const historyItems = [];

    for (let file of files) {
      const png = await convertToPng(file);
      newResults.push(png);
      historyItems.push({
        original_name: file.name,
        converted_name: png.name,
        date: new Date().toISOString(),
      });
    }

    setResults(newResults);

    // Save conversion history locally
    const existingHistory = JSON.parse(localStorage.getItem("converter_history") || "[]");
    localStorage.setItem(
      "converter_history",
      JSON.stringify([...historyItems, ...existingHistory].slice(0, 50))
    );

    setConverting(false);
  };

  const convertToPng = (file) =>
    new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        const url = canvas.toDataURL("image/png");
        URL.revokeObjectURL(objectUrl);
        resolve({
          url,
          name: file.name.replace(/\.(jpg|jpeg)$/i, ".png"),
          originalSize: formatBytes(file.size),
          width: img.width,
          height: img.height,
        });
      };
      img.src = objectUrl;
    });

  const downloadSingle = (url, name) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const downloadAll = () => {
    results.forEach((res, i) => {
      setTimeout(() => {
        downloadSingle(res.url, res.name);
      }, i * 300);
    });
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  return (
    <div style={styles.pageContainer}>
      {/* ===== NAVBAR ===== */}
      <nav style={styles.navbar}>
        <div style={styles.navLogo}>
          <div style={styles.logoBadge}>🖼️</div>
          <span style={styles.logoText}>JPG → PNG Converter</span>
        </div>
        <div style={styles.navActions}>
          <div style={styles.statusPill}>
            <span style={styles.statusDot}></span>
            <span>Client Engine Ready</span>
          </div>
          <Link to="/admin" style={styles.adminBtn}>
            📊 Admin Analytics
          </Link>
        </div>
      </nav>

      {/* ===== HERO SECTION ===== */}
      <header style={styles.hero}>
        <div style={styles.badgePill}>
          ✨ 100% Free • Instant • Private (Runs in Browser)
        </div>
        <h1 style={styles.heroTitle}>
          Convert JPG to PNG <span style={styles.gradientText}>Instantly</span>
        </h1>
        <p style={styles.heroSubtitle}>
          High-quality image conversion directly inside your browser. No registration, no file size limits, and zero server uploads.
        </p>

        {/* STEP CARDS */}
        <div style={styles.stepsContainer}>
          <div style={styles.stepCard}>
            <div style={styles.stepIcon}>📁</div>
            <h4 style={styles.stepTitle}>1. Select JPGs</h4>
            <p style={styles.stepDesc}>Drag & drop or browse your JPG files</p>
          </div>

          <div style={styles.stepCard}>
            <div style={{ ...styles.stepIcon, background: "rgba(139, 92, 246, 0.1)", color: "#8b5cf6" }}>⚡</div>
            <h4 style={styles.stepTitle}>2. Instant Convert</h4>
            <p style={styles.stepDesc}>One-click browser canvas rendering</p>
          </div>

          <div style={{ ...styles.stepCard, borderRight: "none" }}>
            <div style={{ ...styles.stepIcon, background: "rgba(16, 185, 129, 0.1)", color: "#10b981" }}>📥</div>
            <h4 style={styles.stepTitle}>3. Download PNG</h4>
            <p style={styles.stepDesc}>Save lossless high resolution PNGs</p>
          </div>
        </div>
      </header>

      {/* ===== MAIN CONVERTER CARD ===== */}
      <main style={styles.mainCard}>
        {/* DROP ZONE */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          style={{
            ...styles.dropZone,
            ...(isDragging ? styles.dropZoneActive : {}),
          }}
        >
          <div style={styles.dropIconContainer}>
            <span style={{ fontSize: 36 }}>☁️</span>
          </div>
          <h3 style={styles.dropTitle}>Drag & Drop your JPG images here</h3>
          <p style={styles.dropSub}>Supports .jpg and .jpeg files</p>

          <label style={styles.browseBtn}>
            <span style={{ marginRight: 6 }}>📂</span> Browse Files
            <input
              type="file"
              hidden
              multiple
              accept="image/jpeg,image/jpg"
              onChange={handleFileSelect}
            />
          </label>
        </div>

        {/* SELECTED FILES LIST */}
        {files.length > 0 && (
          <div style={styles.fileListSection}>
            <div style={styles.fileListHeader}>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                Selected Files ({files.length})
              </h4>
              <button style={styles.clearBtn} onClick={clearAll}>
                Clear All
              </button>
            </div>

            <div style={styles.fileGrid}>
              {files.map((file, index) => (
                <div key={index} style={styles.fileChip}>
                  <div style={styles.fileChipIcon}>🖼️</div>
                  <div style={styles.fileChipInfo}>
                    <span style={styles.fileChipName}>{file.name}</span>
                    <span style={styles.fileChipSize}>{formatBytes(file.size)}</span>
                  </div>
                  <button
                    style={styles.fileChipRemove}
                    onClick={() => removeFile(index)}
                    title="Remove file"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>

            {/* ACTION BUTTON */}
            <div style={{ marginTop: 24 }}>
              <button
                style={{
                  ...styles.convertBtn,
                  ...(converting || !files.length ? styles.btnDisabled : {}),
                }}
                disabled={converting || !files.length}
                onClick={convertAll}
              >
                {converting ? (
                  <span style={styles.btnFlex}>
                    <span style={styles.spinner}></span> Converting Images...
                  </span>
                ) : (
                  <span>🚀 Convert {files.length} Image(s) to PNG</span>
                )}
              </button>
            </div>
          </div>
        )}

        {/* CONVERTED RESULTS SECTION */}
        {results.length > 0 && (
          <div style={styles.resultsContainer}>
            <div style={styles.resultsHeader}>
              <div>
                <h3 style={{ fontSize: 18, fontWeight: 800, margin: 0 }}>
                  🎉 Converted PNG Files ({results.length})
                </h3>
                <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 0" }}>
                  Ready to download in full resolution
                </p>
              </div>

              {results.length > 1 && (
                <button style={styles.downloadAllBtn} onClick={downloadAll}>
                  ⬇ Download All ({results.length})
                </button>
              )}
            </div>

            <div style={styles.resultsGrid}>
              {results.map((res, i) => (
                <div key={i} style={styles.resultCard}>
                  <div style={styles.resultPreviewBox}>
                    <img src={res.url} alt={res.name} style={styles.resultImg} />
                  </div>
                  <div style={styles.resultDetails}>
                    <div style={styles.resultMeta}>
                      <span style={styles.resultName}>{res.name}</span>
                      <span style={styles.resultSub}>
                        {res.width} × {res.height} px • {res.originalSize}
                      </span>
                    </div>
                    <button
                      style={styles.downloadSingleBtn}
                      onClick={() => downloadSingle(res.url, res.name)}
                    >
                      ⬇ Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* ===== FEATURES / TRUST SECTION ===== */}
      <section style={styles.featuresSection}>
        <div style={styles.featuresGrid}>
          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>🔒</div>
            <h4 style={styles.featureTitle}>100% Private & Secure</h4>
            <p style={styles.featureDesc}>Your images stay on your device. Zero server uploads.</p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>⚡</div>
            <h4 style={styles.featureTitle}>Lightning Fast</h4>
            <p style={styles.featureDesc}>GPU-accelerated browser HTML5 canvas engine.</p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>💎</div>
            <h4 style={styles.featureTitle}>Lossless Quality</h4>
            <p style={styles.featureDesc}>Preserves maximum image clarity and detail.</p>
          </div>

          <div style={styles.featureCard}>
            <div style={styles.featureIcon}>📱</div>
            <h4 style={styles.featureTitle}>Fully Responsive</h4>
            <p style={styles.featureDesc}>Works flawlessly on Mobile, Tablet & Desktop.</p>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={styles.footer}>
        <p>© 2026 JPG → PNG Converter • Designed for Speed & Privacy</p>
      </footer>
    </div>
  );
}

/* ==========================================================================
   CSS IN JS STYLES - RESPONSIVE & PREMIUM AESTHETICS
   ========================================================================== */

const styles = {
  pageContainer: {
    minHeight: "100vh",
    padding: "16px 20px 40px",
    maxWidth: 1100,
    margin: "0 auto",
  },
  navbar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 24px",
    background: "rgba(255, 255, 255, 0.85)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: 20,
    border: "1px solid rgba(255, 255, 255, 0.9)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
    marginBottom: 32,
    flexWrap: "wrap",
    gap: 12,
  },
  navLogo: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logoBadge: {
    width: 38,
    height: 38,
    borderRadius: 12,
    background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 18,
    boxShadow: "0 6px 15px rgba(99, 102, 241, 0.3)",
  },
  logoText: {
    fontSize: 19,
    fontWeight: 800,
    background: "linear-gradient(135deg, #0f172a, #334155)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  navActions: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    flexWrap: "wrap",
  },
  statusPill: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "6px 14px",
    borderRadius: 999,
    background: "rgba(16, 185, 129, 0.1)",
    color: "#059669",
    fontSize: 13,
    fontWeight: 600,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#10b981",
    boxShadow: "0 0 8px #10b981",
  },
  adminBtn: {
    padding: "10px 18px",
    background: "linear-gradient(135deg, #0f172a, #1e293b)",
    color: "#fff",
    borderRadius: 12,
    textDecoration: "none",
    fontSize: 14,
    fontWeight: 700,
    boxShadow: "0 6px 20px rgba(15, 23, 42, 0.15)",
    transition: "transform 0.2s ease",
  },
  hero: {
    textAlign: "center",
    maxWidth: 820,
    margin: "0 auto 36px",
  },
  badgePill: {
    display: "inline-block",
    padding: "6px 16px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #e0e7ff, #f3e8ff)",
    color: "#4338ca",
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 16,
    border: "1px solid rgba(99, 102, 241, 0.2)",
  },
  heroTitle: {
    fontSize: "clamp(30px, 5vw, 48px)",
    fontWeight: 800,
    color: "#0f172a",
    lineHeight: 1.2,
    marginBottom: 12,
    letterSpacing: "-0.5px",
  },
  gradientText: {
    background: "linear-gradient(135deg, #4f46e5, #8b5cf6)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
  heroSubtitle: {
    fontSize: "clamp(15px, 2.5vw, 17px)",
    color: "#64748b",
    lineHeight: 1.6,
    marginBottom: 28,
  },
  stepsContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
    background: "rgba(255, 255, 255, 0.7)",
    padding: 12,
    borderRadius: 20,
    border: "1px solid rgba(255, 255, 255, 0.8)",
    boxShadow: "0 10px 30px rgba(0,0,0,0.02)",
  },
  stepCard: {
    padding: 16,
    borderRadius: 14,
    background: "#fff",
    textAlign: "center",
    boxShadow: "0 4px 15px rgba(0,0,0,0.02)",
  },
  stepIcon: {
    width: 42,
    height: 42,
    borderRadius: 12,
    background: "rgba(79, 70, 229, 0.1)",
    color: "#4f46e5",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 20,
    margin: "0 auto 10px",
  },
  stepTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 4px",
  },
  stepDesc: {
    fontSize: 13,
    color: "#64748b",
    margin: 0,
  },
  mainCard: {
    background: "rgba(255, 255, 255, 0.92)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    padding: "clamp(20px, 4vw, 36px)",
    borderRadius: 28,
    border: "1px solid rgba(255, 255, 255, 0.95)",
    boxShadow: "0 25px 60px -15px rgba(0, 0, 0, 0.08)",
    marginBottom: 40,
  },
  dropZone: {
    border: "2px dashed #cbd5e1",
    padding: "40px 20px",
    borderRadius: 20,
    textAlign: "center",
    background: "linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)",
    transition: "all 0.25s ease",
    cursor: "pointer",
  },
  dropZoneActive: {
    borderColor: "#6366f1",
    background: "rgba(99, 102, 241, 0.06)",
    transform: "scale(1.01)",
  },
  dropIconContainer: {
    width: 64,
    height: 64,
    borderRadius: 20,
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px",
    boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
  },
  dropTitle: {
    fontSize: "clamp(16px, 3vw, 20px)",
    fontWeight: 700,
    color: "#1e293b",
    margin: "0 0 6px",
  },
  dropSub: {
    color: "#64748b",
    fontSize: 14,
    margin: "0 0 20px",
  },
  browseBtn: {
    padding: "12px 24px",
    background: "linear-gradient(135deg, #4f46e5, #6366f1)",
    color: "#fff",
    borderRadius: 999,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    fontWeight: 700,
    fontSize: 15,
    boxShadow: "0 8px 20px rgba(79, 70, 229, 0.3)",
    transition: "all 0.2s ease",
  },
  fileListSection: {
    marginTop: 28,
    paddingTop: 24,
    borderTop: "1px solid #e2e8f0",
  },
  fileListHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  clearBtn: {
    background: "#fee2e2",
    color: "#ef4444",
    border: "none",
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
  },
  fileGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
    gap: 12,
  },
  fileChip: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    background: "#f8fafc",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },
  fileChipIcon: {
    fontSize: 20,
  },
  fileChipInfo: {
    flex: 1,
    overflow: "hidden",
  },
  fileChipName: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#1e293b",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  fileChipSize: {
    fontSize: 11,
    color: "#64748b",
  },
  fileChipRemove: {
    background: "transparent",
    border: "none",
    color: "#94a3b8",
    cursor: "pointer",
    fontSize: 16,
    padding: 4,
  },
  convertBtn: {
    width: "100%",
    padding: "16px",
    borderRadius: 16,
    background: "linear-gradient(135deg, #4f46e5, #8b5cf6)",
    color: "#fff",
    border: "none",
    fontSize: 17,
    fontWeight: 800,
    cursor: "pointer",
    boxShadow: "0 12px 30px rgba(79, 70, 229, 0.35)",
    transition: "all 0.25s ease",
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  btnFlex: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  spinner: {
    width: 20,
    height: 20,
    border: "3px solid rgba(255,255,255,0.3)",
    borderTop: "3px solid #fff",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
  },
  resultsContainer: {
    marginTop: 32,
    paddingTop: 28,
    borderTop: "2px dashed #e2e8f0",
  },
  resultsHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    flexWrap: "wrap",
    gap: 12,
  },
  downloadAllBtn: {
    padding: "10px 20px",
    background: "linear-gradient(135deg, #10b981, #059669)",
    color: "#fff",
    border: "none",
    borderRadius: 12,
    fontWeight: 700,
    fontSize: 14,
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(16, 185, 129, 0.3)",
  },
  resultsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 16,
  },
  resultCard: {
    background: "#fff",
    borderRadius: 16,
    border: "1px solid #e2e8f0",
    padding: 14,
    boxShadow: "0 8px 20px rgba(0,0,0,0.03)",
  },
  resultPreviewBox: {
    height: 160,
    borderRadius: 10,
    background: "#f1f5f9",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
    marginBottom: 12,
  },
  resultImg: {
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit: "contain",
  },
  resultDetails: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
  },
  resultMeta: {
    overflow: "hidden",
  },
  resultName: {
    display: "block",
    fontSize: 14,
    fontWeight: 700,
    color: "#0f172a",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  resultSub: {
    fontSize: 12,
    color: "#64748b",
  },
  downloadSingleBtn: {
    padding: "8px 14px",
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  featuresSection: {
    maxWidth: 1000,
    margin: "0 auto 40px",
  },
  featuresGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 16,
  },
  featureCard: {
    background: "rgba(255, 255, 255, 0.75)",
    backdropFilter: "blur(10px)",
    padding: 20,
    borderRadius: 18,
    border: "1px solid rgba(255, 255, 255, 0.9)",
  },
  featureIcon: {
    fontSize: 24,
    marginBottom: 8,
  },
  featureTitle: {
    fontSize: 15,
    fontWeight: 700,
    color: "#0f172a",
    margin: "0 0 4px",
  },
  featureDesc: {
    fontSize: 13,
    color: "#64748b",
    lineHeight: 1.5,
    margin: 0,
  },
  footer: {
    textAlign: "center",
    color: "#94a3b8",
    fontSize: 13,
    paddingTop: 20,
  },
};
